// @ts-strict-ignore
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    CreateIntegrationRequestDataType,
    CreateIntegrationRequestType,
    tryCreateIntegrationRequest,
} from '../../actions/integrations';
import { getError, getIsLoading, getUpdated } from '../../helpers';
import { authTypeForIntegrationType, shouldShowConfigurationDuringOnboarding } from '../../helpers/integrationHelpers';
import { AppComponentProps } from '../../typescriptHelpers';
import { IntegrationErrorType, IntegrationType, Integration, AppPlatform } from '../../types';
import { globalMessage } from '../../utils/globalMessage';

interface useAddIntegrationModalData {
    setShowIntegrationModal: React.Dispatch<React.SetStateAction<boolean>>;
    integrationModalProps: AddIntegrationModalProps;
}

export interface AddIntegrationModalProps {
    orgId: string;
    isLoading: boolean;
    integrationType: IntegrationType;
    open: boolean;
    addIntegration: (integrationType: IntegrationType, data: CreateIntegrationRequestDataType) => void;
    onCancel: () => void;
}

interface useAddIntegrationModalProps extends AppComponentProps {
    selectedIntegration?: Integration;
    integrationType: IntegrationType;
    setShowUpdateIntegrationModal: React.Dispatch<React.SetStateAction<boolean>>;
    platform?: AppPlatform;
    shouldUseQuickStartCIInstallation?: boolean;
}

export const useAddIntegrationModal = ({
    orgId,
    appId,
    selectedIntegration,
    integrationType,
    setShowUpdateIntegrationModal,
    platform,
    shouldUseQuickStartCIInstallation,
}: useAddIntegrationModalProps): useAddIntegrationModalData => {
    const dispatch = useDispatch();

    const suppressInstallationSetupModal = !!platform || shouldUseQuickStartCIInstallation;

    const [showIntegrationModal, setShowIntegrationModal] = React.useState(false);
    const [integrationAuthType, setIntegrationAuthType] = React.useState(null);

    const addIntegrationIsLoading = useSelector((state: RootState) => getIsLoading(integrationAuthType, state));
    const addIntegrationSuccess = useSelector((state: RootState) =>
        getUpdated(integrationAuthType, state, selectedIntegration?.installationId ?? null),
    );
    const addIntegrationError = useSelector((state: RootState) => getError(integrationAuthType, state));

    const addIntegration = (integrationType: IntegrationType, data: CreateIntegrationRequestDataType) => {
        const authType = authTypeForIntegrationType(integrationType);
        setIntegrationAuthType(authType);

        const request: CreateIntegrationRequestType = {
            orgId: orgId,
            appId: appId,
            integrationId: integrationType,
            data: {
                ...data,
                platform,
            },
            isQuickStartCI: shouldUseQuickStartCIInstallation,
        };

        if (authType) {
            dispatch(tryCreateIntegrationRequest(authType, request));
        } else {
            globalMessage.error('Unrecognized auth type for integration type');
        }
    };

    React.useEffect(() => {
        if (addIntegrationSuccess) {
            setShowIntegrationModal(false);
            if (
                !suppressInstallationSetupModal &&
                shouldShowConfigurationDuringOnboarding(integrationType) &&
                selectedIntegration?.integrationError?.status === IntegrationErrorType.IncompleteData
            ) {
                setShowUpdateIntegrationModal(true);
            }
            globalMessage.success('Integration connected!');
        }
    }, [addIntegrationSuccess]);

    React.useEffect(() => {
        if (addIntegrationError) {
            globalMessage.error(addIntegrationError);
        }
    }, [addIntegrationError]);

    const integrationModalProps = React.useMemo(() => {
        return {
            orgId,
            appId,
            isLoading: addIntegrationIsLoading,
            integrationType,
            open: showIntegrationModal,
            addIntegration,
            onCancel: () => setShowIntegrationModal(false),
        };
    }, [orgId, appId, showIntegrationModal, addIntegrationIsLoading]);

    return { setShowIntegrationModal, integrationModalProps };
};
